import type { DDELicenseResponse } from '@img-arena/consoles-api-dde'
import { ddeApi } from '@/service/ddeApi'

import { createBaseTableStore } from '../table'
import { FetchApiArgs, FetchApiResponse } from '../table/table.types'
import {
  buildSportsMap,
  fetchSportSeasons,
  mapLicensesWithSeasons
} from './licenses.dde.store.helpers'

/**
 * Fetches operator licenses, enriches them with season data, and returns them in a table-compatible format.
 *
 * @param {FetchApiArgs<{ operatorId: number }, string, string>} args - Arguments for fetching operator licenses.
 * @returns {Promise<FetchApiResponse<License>>} Promise resolving to the API response with enriched licenses.
 */
const fetchApi = async (
  args: FetchApiArgs<{ operatorId: number }, string, string>
): Promise<FetchApiResponse<DDELicenseResponse>> => {
  const {
    signal,
    additionalParams: { operatorId }
  } = args

  const { data } = await ddeApi.endpoints.operators.getOperatorLicenses(operatorId, signal)

  if (Array.isArray(data) && data.length > 0) {
    const sportsMap = buildSportsMap(data)
    const sports = Object.values(sportsMap)
    const sportSeasonsMap = await fetchSportSeasons(sports, signal)
    const licensesWithSeasons = mapLicensesWithSeasons(data, sportSeasonsMap)
    return {
      content: licensesWithSeasons,
      totalElements: licensesWithSeasons.length
    }
  }

  return {
    content: data,
    totalElements: data.length
  }
}

export const useOperatorLicensesStoreDDE = createBaseTableStore({
  storeName: 'DDE Operator Licenses',
  fetchApi
})
